import React, {useEffect, useState} from 'react'
import Drawer from 'react-modern-drawer'
import {useAuth} from '../../../../modules/auth'
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  runTransaction,
} from 'firebase/firestore'
import db from '../../../../../services/firebase'
import {useNavigate, useLocation} from 'react-router-dom'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import {exchangeCodeForSlackToken, createSlackService} from 'app/Integrations/Slack'
import DrawerHeader from '../drawer-components/DrawerHeader'
import {DrawerContent} from '../drawer-components/DrawerContent'
import DrawerFooter from '../drawer-components/DrawerFooter'
import {AddDrawerIntegrationProps} from '../utils/types'

const SlackDrawer: React.FC<AddDrawerIntegrationProps> = ({id, isOpen, onClose}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [openAccordion, setOpenAccordion] = useState(false)
  const [openAccordion2, setOpenAccordion2] = useState(false)
  const {currentUser} = useAuth()
  const [integration, setIntegration] = useState<any[]>([])
  const isConnected = integration.length > 0 && integration[0]?.status === 'Connected'
  const isChannelLocked = integration.length > 0 && integration[0]?.channelLocked
  const navigate = useNavigate()
  const location = useLocation()
  const [selectedChannel, setSelectedChannel] = useState('')
  const [channels, setChannels] = useState<any[]>([])
  const [isSelectingChannel, setIsSelectingChannel] = useState(false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isOpen && id) {
      getIntegration()
    }
  }, [id, isOpen])

  useEffect(() => {
    // Handle OAuth callback
    const query = new URLSearchParams(location.search)
    const code = query.get('code')
    const channelId = query.get('channelId') || ''

    if (code && isOpen) {
      handleOAuthCallback(code, channelId)
    }
  }, [location, isOpen])

  useEffect(() => {
    if (isConnected && integration[0]) {
      setChannels(integration[0].channels || [])
      setSelectedChannel(integration[0].selectedChannel || '')
    } else {
      setChannels([])
      setSelectedChannel('')
    }
  }, [integration, isConnected])

  useEffect(() => {
    if (
      isOpen &&
      isConnected &&
      !isChannelLocked &&
      channels.length > 0 &&
      !selectedChannel
    ) {
      setFormErrors((prev) => ({...prev, selectedChannel: 'Please select a group'}))
    } else {
      setFormErrors((prev) => ({...prev, selectedChannel: ''}))
    }
  }, [isOpen, isConnected, isChannelLocked, channels, selectedChannel])

  const getIntegration = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!id) return
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data()
          if (data.name === 'Slack') {
            const integrationData = {
              ...data,
              id: doc.id,
              name: data.name,
              team_id: data.team_id,
              channels: data.channels,
              selectedChannel: data.selectedChannel,
              channelLocked: data.channelLocked || false,
            }
            setIntegration([integrationData])
            setChannels(data.channels || [])
            setSelectedChannel(data.selectedChannel || '')
          }
        }
      })
      return unsubscribe
    } catch (error) {
      console.error('Error getting integration:', error)
    }
  }

  const handleOAuthCallback = async (code: string, channelId?: string) => {
    setIsLoading(true)
    try {
      await exchangeCodeForSlackToken(code, currentUser, navigate, channelId)
      showSuccessToast({message: 'Successfully authenticated with Slack'})
    } catch (error) {
      showErrorToast({message: 'Slack authentication failed. Try again!'})
    } finally {
      setIsLoading(false)
      // Clear query parameters
      navigate('/integrations', {replace: true})
    }
  }

  const handleConnect = async (): Promise<void> => {
    localStorage.setItem('pendingIntegration', 'Slack')
    const clientId = process.env.REACT_APP_SLACK_CLIENT_ID
    const redirectUri = process.env.REACT_APP_SLACK_REDIRECT_URI || ''
    const scope = 'chat:write,users:read,channels:read,channels:join,groups:read'
    const oauthUrl = `https://slack.com/oauth/v2/authorize?client_id=${clientId}&scope=${encodeURIComponent(
      scope
    )}&redirect_uri=${encodeURIComponent(redirectUri)}`

    window.location.href = oauthUrl
    return
  }

  const handleDisconnect = async () => {
    try {
      setIsLoading(true)
      const tenantId = currentUser?.tenantId || ''
      if (!id || !tenantId) return
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      await deleteDoc(integrationRef)
      const tenantRef = doc(db, 'tenants', tenantId)
      const vendorsRef = collection(tenantRef, 'vendors')
      const vendorQuery = query(vendorsRef, where('vendor_name', '==', 'Slack'))
      const vendorSnapshot = await getDocs(vendorQuery)

      const accessReviewRef = doc(db, 'tenants', tenantId, 'access_review', 'slack')
      await deleteDoc(accessReviewRef)

      const deleteVendorPromises = vendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id))
      })
      await Promise.all([...deleteVendorPromises])
      localStorage.removeItem('slack_access_token')
      showSuccessToast({message: 'Successfully disconnected from Slack'})
      setIntegration([])
      onClose()
    } catch (error) {
      showErrorToast({message: 'Failed to disconnect from Slack'})
    } finally {
      setIsLoading(false)
    }
  }

  const handleChannelSelect = async (channelId: string) => {

    if (!channelId || isChannelLocked) {
      setFormErrors((prev) => ({...prev, selectedChannel: 'Please select a group'}))
      return
    }
    if (!id || !currentUser || isChannelLocked) return
    setFormErrors((prev) => ({...prev, selectedChannel: ''}))
    setIsSelectingChannel(true)
    setSelectedChannel(channelId)

    const currentDate = new Date()
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth() + 1
    const day = currentDate.getDate()
    const todaysDate = `${month}/${day}/${year}`

    try {
      const tenantId = currentUser.tenantId || ''
      const integrationData = integration[0]
      const slackService = createSlackService(integrationData.access_token, integrationData.team_id)

      // Join the channel
      const joinResult = await slackService.joinChannel(channelId)
      if (!joinResult.success && joinResult.error !== 'already_in_channel') {
        showErrorToast({message: `Failed to join channel: ${joinResult.error}`})
        setSelectedChannel('')
        setIsSelectingChannel(false)
        return
      }

      // Run Firestore updates in a transaction
      await runTransaction(db, async (transaction) => {
        const docRef = doc(db, 'tenants', tenantId, 'integrations', id)
        transaction.update(docRef, {
          selectedChannel: channelId,
          channelLocked: true,
          updatedAt: serverTimestamp(),
        })

        const tenantRef = doc(db, 'tenants', tenantId)
        const activitiesRef = collection(tenantRef, 'activities')
        const selectedChannelName =
          channels.find((channel) => channel.id === channelId)?.name || channelId

        transaction.set(doc(activitiesRef), {
          message: `${currentUser?.userName} has selected the ${selectedChannelName} channel in the Slack integration.`,
          timestamp: serverTimestamp(),
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          action: 'Slack channel selected',
          target: 'Integration',
          email: currentUser?.email,
          username: currentUser?.userName,
          date: todaysDate,
        })
      })

      showSuccessToast({
        message: `Slack channel ${
          channels.find((channel) => channel.id === channelId)?.name || channelId
        } selected and joined successfully`,
      })
    } catch (error) {
      console.error('Error selecting channel:', error)
      showErrorToast({message: 'Failed to select channel'})
      setSelectedChannel('')
    } finally {
      setIsSelectingChannel(false)
    }
  }

  return (
    <Drawer open={isOpen} onClose={onClose} direction='right' className='' size='45%'>
      <DrawerHeader title='Slack' onClose={onClose} />

      <div
        className='scroll-y overflow-auto p-6 mb-10'
        tabIndex={-1}
        style={{
          height: 'calc(100vh - 120px)',
          paddingBottom: '80px',
        }}
      >
        <div className='form'>
          <DrawerContent
            title='Slack'
            description='Team Communication and Collaboration'
            logo='/integrations/Slack.svg'
            integration={integration}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            openAccordion2={openAccordion2}
            setOpenAccordion2={setOpenAccordion2}
            permissionInfo='SecureSlate will be granted access to your Slack workspace to read channels, join channels, and send messages.'
            accessTypeinfo='You need to authenticate with Slack using OAuth to grant access to your workspace.'
          />

          <div className='mb-10'>
            <label className='form-label'>
              Select Channel{' '}
              {isSelectingChannel && (
                 <span className='badge px-3 rounded-pill ms-4' style={{border: '1px solid #E1E1E1'}}>
                 <span className='fs-7'>Updating...</span>
               </span>
              )}
            </label>
            {isConnected && channels.length > 0 ? (
              isChannelLocked ? (
                <>
                  <div className='form-control bg-light'>
                    {channels.find((channel) => channel.id === selectedChannel)?.name ||
                      'Selected Channel'}
                  </div>
                  {isChannelLocked && (
                    <small className='text-muted'>
                      Channel selection is locked. To change, reconnect the integration.
                    </small>
                  )}
                </>
              ) : (
                <div className='position-relative'>
                  <select
                    className={`form-select ${formErrors.selectedChannel ? 'is-invalid' : ''}`}
                    value={selectedChannel}
                    onChange={(e) => handleChannelSelect(e.target.value)}
                    disabled={isSelectingChannel || !isConnected || isChannelLocked}
                  >
                    <option value=''>Select a channel</option>
                    {channels.map((channel) => (
                      <option key={channel.id} value={channel.id}>
                        {channel.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.selectedChannel && (
                    <span className='text-danger'>{formErrors.selectedChannel}</span>
                  )}
                </div>
              )
            ) : (
              <div className='form-control bg-light text-muted'>
                {isConnected ? 'No channels available' : 'Connect to Slack to select a channel'}
              </div>
            )}
          </div>
        </div>
      </div>
      <DrawerFooter
        isConnected={isConnected}
        isLoading={isLoading}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        customActions={!isConnected}
      />
    </Drawer>
  )
}

export default SlackDrawer
